/**
 * Дополнительные работы хозяйств — `extra-works-<cycleId>.json`, см.
 * `prototype/WATER-WORKS-SPEC.md` («Дополнительные работы»). Общий модуль
 * для всех экранов, которые их показывают: кабинет фермера (`/my/history`,
 * `/my/fields`, `/my/today`), `FarmDetail`, `CycleDetail`, `ContourDetail`.
 * Один модуль, а не пять копий — по тому же аргументу, что и `./farmer.ts`:
 * разъехавшиеся копии начали бы читать «сверх шаблона» по-разному.
 *
 * Тип `ExtraWork` и `FILES.extraWorks` — канонические, из `src/data/types.ts`
 * и `src/data/loader.ts` (добавлены параллельно этому экрану; сверено — форма
 * один-в-один со спекой, локальный fallback-дубликат больше не нужен и был
 * удалён). Всё остальное здесь — загрузка обоих циклов одним `TrackData`,
 * фильтры по хозяйству/контуру и подписи происхождения, которых в
 * каноническом слое нет.
 *
 * Правило спеки, которое держит весь модуль: внеплановая работа НЕ входит в
 * знаменатели шаблонных обязательств. Ни одна функция здесь не возвращает
 * число, которое можно молча сложить со счётом DoneSplit.
 *
 * Правило владельца (мининг источников, research/2026-07-30 §6, A.4):
 * добровольная работа не делает соседей «отстающими» — сводки «сверх
 * шаблона» показывают факт по одному хозяйству/циклу и НЕ сравнивают
 * хозяйства между собой по числу доп. работ. Поэтому в модуле нет функции
 * ранжирования/сортировки хозяйств по count — только факт и разбивка по
 * origin одного среза (хозяйства, цикла или контура).
 */

import { useMemo } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Droplet, Hand, ShieldAlert } from 'lucide-react';
import { useTrackData } from '../../data/useTrackData';
import type { TrackData } from '../../data/useTrackData';
import { FILES } from '../../data/loader';
import { defineMessages } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';
import { dateShort, num } from '../../lib/format';
import type {
  ExtraWork,
  ExtraWorkOrigin,
  QuarantineFollowupResult,
  QuarantineRequirement,
  QuarantineStatus,
} from '../../data/types';

export type { ExtraWork, ExtraWorkKind, ExtraWorkOrigin } from '../../data/types';

/**
 * Прототип живёт ровно двумя циклами (DESIGN-BRIEF «Мир данных»); id стабильны
 * на обоих треках — та же пара, что в `farmer.ts` и в детальных страницах.
 */
const CYCLE_WHEAT = 'cycle-wheat-2025';
const CYCLE_COTTON = 'cycle-cotton-2026';

const m = defineMessages({
  badgeManual: { id: 'farmer.extra.badge.manual', defaultMessage: 'решение фермера', description: 'Бейдж происхождения добровольной работы: фермер сделал её сам, по своему решению; узкий бейдж в колонке таблицы' },
  badgeQuarantine: { id: 'farmer.extra.badge.quarantine', defaultMessage: 'по предписанию', description: 'Бейдж происхождения добровольной работы: сделана во исполнение карантинного предписания; узкий бейдж' },
  badgeOffPlanWater: { id: 'farmer.extra.badge.offPlanWater', defaultMessage: 'вне плана', description: 'Бейдж происхождения: полив вне плана; узкий бейдж' },
  detailManual: { id: 'farmer.extra.detail.manual', defaultMessage: 'сверх плана, по решению хозяйства', description: 'Пояснение под бейджем происхождения добровольной работы (решение хозяйства)' },
  detailQuarantine: { id: 'farmer.extra.detail.quarantine', defaultMessage: 'во исполнение предписания карантина', description: 'Пояснение под бейджем происхождения добровольной работы (предписание карантина)' },
  detailOffPlanWater: { id: 'farmer.extra.detail.offPlanWater', defaultMessage: 'полив вне плана, без плановой работы', description: 'Пояснение под бейджем происхождения: полив, которого нет в плане работ' },
  originUnknown: { id: 'farmer.extra.origin.unknown', defaultMessage: 'происхождение не названо', description: 'Пояснение, когда источник не назвал происхождение добровольной работы' },
  outsideNote: {
    id: 'farmer.extra.outsideNote',
    defaultMessage: 'Добровольная работа идёт сверх плана: она считается отдельной строкой и в счёт плановых работ не входит.',
    description: 'Пояснение под заголовком блока добровольных работ (хозяйство, цикл, контур): почему эти работы не меняют счёт плановых',
  },
  followupReduced: { id: 'farmer.extra.followup.reduced', defaultMessage: 'очаг снижен', description: 'Итог повторного карантинного контроля: очаг поражения уменьшился' },
  followupUnchanged: { id: 'farmer.extra.followup.unchanged', defaultMessage: 'без изменений', description: 'Итог повторного карантинного контроля: очаг не изменился' },
  followupEliminated: { id: 'farmer.extra.followup.eliminated', defaultMessage: 'устранено', description: 'Итог повторного карантинного контроля: очаг устранён (по замеру инспектора)' },
  reqBoth: { id: 'farmer.extra.requirement.both', defaultMessage: 'предписание от {issued}, срок — {deadline}', description: 'Строка о карантинном предписании; {issued}/{deadline} — даты, уже отформатированные («15 авг»)' },
  reqIssued: { id: 'farmer.extra.requirement.issued', defaultMessage: 'предписание от {issued}', description: 'Строка о карантинном предписании без срока; {issued} — дата выдачи' },
  reqDeadline: { id: 'farmer.extra.requirement.deadline', defaultMessage: 'предписание, срок — {deadline}', description: 'Строка о карантинном предписании без даты выдачи; {deadline} — срок' },
  countSuffix: {
    id: 'farmer.extra.countSuffix',
    defaultMessage: '+{count, plural, one {# добровольная} few {# добровольных} many {# добровольных} other {# добровольных}}',
    description: 'Хвост статуса контура после « · »: сколько добровольных работ сделано; «+2 добровольных» (стоит после слова «работа» в строке)',
  },
  filesMissing: { id: 'farmer.extra.filesMissing', defaultMessage: 'файлы добровольных работ не найдены', description: 'Причина пустого блока: данные о добровольных работах не загрузились' },
  summaryLine: {
    id: 'farmer.extra.summaryLine',
    defaultMessage: '{manual} по решению хозяйства, {quarantine} по предписаниям, {water} поливов вне плана',
    description: 'Раскладка добровольных работ хозяйства по происхождению одной строкой; все три аргумента — числа, уже отформатированные',
  },
});

/* --------------------------------------------------------------- подписи */

/**
 * Бейдж колонки «Происхождение» — СТРОГО ось происхождения и ничего больше.
 * Каталог работ на `/cycles` разводит две оси и прямо предупреждает «другая
 * ось, чем происхождение выше»: происхождение (откуда взялась работа) и
 * применимость (обязательная / условная / ситуационная / рекомендованная /
 * добровольная). Прежние подписи «добровольная»/«ситуационная» подставляли в
 * колонку происхождения значения ЧУЖОЙ оси — ровно та ошибка, которую каталог
 * запрещает. Здесь остаются три значения `ExtraWorkOrigin`, слово в слово.
 *
 * Медь (`tone="copper"`) — ритм отличия от шаблонных бейджей (sage/outline),
 * не тревога: смысл несёт подпись, а не цвет.
 */
export const ORIGIN_BADGE_LABEL: Record<ExtraWorkOrigin, string> = {
  get manual() {
    return tr(m.badgeManual);
  },
  get q_finding() {
    return tr(m.badgeQuarantine);
  },
  get unplanned_water() {
    return tr(m.badgeOffPlanWater);
  },
};

/**
 * Уточнение под короткой меткой — не синоним бейджа, а объяснение, что
 * означает это происхождение. Повторять бейдж другими словами значило бы
 * занять строку, ничего не сказав.
 */
export const ORIGIN_DETAIL_LABEL: Record<ExtraWorkOrigin, string> = {
  get manual() {
    return tr(m.detailManual);
  },
  get q_finding() {
    return tr(m.detailQuarantine);
  },
  get unplanned_water() {
    return tr(m.detailOffPlanWater);
  },
};

/**
 * Тот же словарь для слоёв, прочитанных без доверия к форме (`/water` разбирает
 * `extra-works-*.json` своим терпимым парсером и держит origin строкой).
 */
export function originDetailLabel(origin: string): string {
  return Object.prototype.hasOwnProperty.call(ORIGIN_DETAIL_LABEL, origin)
    ? ORIGIN_DETAIL_LABEL[origin as ExtraWorkOrigin]
    : tr(m.originUnknown);
}

export const ORIGIN_ICON: Record<ExtraWorkOrigin, LucideIcon> = {
  manual: Hand,
  q_finding: ShieldAlert,
  unplanned_water: Droplet,
};

/**
 * Одна строка объяснения, почему добровольная работа не входит в счёт плановых.
 * Её место — пояснение под заголовком, а НЕ сам заголовок.
 */
export function extraOutsideNote(): string {
  return tr(m.outsideNote);
}

/* ------------------------------------------------------------- предписание */

/** Результат повторного контроля — словами, а не кодом `unchanged`. */
export const FOLLOWUP_RESULT_LABEL: Record<QuarantineFollowupResult, string> = {
  get reduced() {
    return tr(m.followupReduced);
  },
  get unchanged() {
    return tr(m.followupUnchanged);
  },
  get eliminated() {
    return tr(m.followupEliminated);
  },
};

/**
 * «Предписание от 15 авг, срок — 24 авг 2026» — даты предписания в локальном
 * формате. ISO в интерфейсе — дефект (DESIGN-BRIEF, «локальный формат»),
 * поэтому генератор кладёт в запись сами даты (`q_issued_on`/`q_deadline`), а
 * не готовую фразу с машинными датами внутри. `null` — если запись не от
 * предписания либо генератор дат не проставил.
 */
export function requirementLine(row: ExtraWork): string | null {
  if (!row.q_issued_on && !row.q_deadline) return null;
  if (row.q_issued_on && row.q_deadline) {
    return tr(m.reqBoth, { issued: dateShort(row.q_issued_on), deadline: dateShort(row.q_deadline) });
  }
  if (row.q_issued_on) return tr(m.reqIssued, { issued: dateShort(row.q_issued_on) });
  return tr(m.reqDeadline, { deadline: dateShort(row.q_deadline as string) });
}

/** Адрес самого предписания: блок «Карантин» на паспорте контура. */
export function requirementHref(row: ExtraWork): string {
  return `/contours/${row.contour_id}#quarantine`;
}

/** «· +2 добровольных» — суффикс для статуса контура; пусто, если count = 0. */
export function extraCountSuffix(count: number): string {
  if (count <= 0) return '';
  return ` · ${tr(m.countSuffix, { count })}`;
}

/* --------------------------------------------------------------- загрузка */

/** Дополнительные работы одного цикла — сырой ресурс, без фильтра по хозяйству. */
export function useExtraWorksForCycle(cycleId: string | null): TrackData<ExtraWork[]> {
  return useTrackData<ExtraWork[]>(cycleId ? FILES.extraWorks(cycleId) : null);
}

/**
 * Дополнительные работы обоих циклов среза, слитые в один ресурс формы
 * `TrackData` — подставляется прямо в `DataGate`, как и любой другой файл
 * фикстур. «ready» — если прочитался хотя бы один цикл (частичный срез — это
 * тоже данные, а не их отсутствие); «missing» — только если не прочитались
 * оба, с честной причиной; «loading» — пока хоть один ещё читается.
 */
export function useExtraWorksBothCycles(): TrackData<ExtraWork[]> {
  const wheat = useExtraWorksForCycle(CYCLE_WHEAT);
  const cotton = useExtraWorksForCycle(CYCLE_COTTON);

  const rows = useMemo(
    () => [...(wheat.data ?? []), ...(cotton.data ?? [])],
    [wheat.data, cotton.data],
  );

  if (wheat.state === 'loading' || cotton.state === 'loading') {
    return { state: 'loading', data: null, reason: null, source: null, track: wheat.track };
  }
  if (wheat.state === 'missing' && cotton.state === 'missing') {
    return {
      state: 'missing',
      data: null,
      reason: wheat.reason ?? cotton.reason ?? tr(m.filesMissing),
      source: null,
      track: wheat.track,
    };
  }
  return {
    state: 'ready',
    data: rows,
    reason: null,
    source: wheat.source === 'stub' || cotton.source === 'stub' ? 'stub' : 'track',
    track: wheat.track,
  };
}

/* ------------------------------------------------------------- срезы данных */

export function farmExtraWorks(rows: ExtraWork[], farmId: string | null): ExtraWork[] {
  if (!farmId) return [];
  return rows.filter((w) => w.farm_id === farmId);
}

export function contourExtraWorks(rows: ExtraWork[], contourId: string | null): ExtraWork[] {
  if (!contourId) return [];
  return rows.filter((w) => w.contour_id === contourId);
}

/**
 * Разбивка ОДНОГО среза (хозяйства, цикла или контура) по origin — три факта
 * рядом, не рейтинг. Не использовать для сравнения нескольких хозяйств между
 * собой: правило владельца прямо это запрещает (см. шапку файла).
 */
export function originBreakdown(rows: ExtraWork[]): Record<ExtraWorkOrigin, number> {
  const out: Record<ExtraWorkOrigin, number> = { unplanned_water: 0, manual: 0, q_finding: 0 };
  for (const w of rows) out[w.origin] += 1;
  return out;
}

/** «M по решению хозяйства, K по предписаниям, L поливов вне плана» — раскладка одной строкой. */
export function originSummaryLine(rows: ExtraWork[]): string {
  const b = originBreakdown(rows);
  return tr(m.summaryLine, { manual: num(b.manual), quarantine: num(b.q_finding), water: num(b.unplanned_water) });
}

/* ------------------------------------------------------- предписания без работы */

export interface OpenQuarantineRequirement {
  contourId: string;
  requirement: QuarantineRequirement;
}

/**
 * Предписания карантина (`quarantine.json` → `statuses[].requirement`) по
 * контурам хозяйства, для которых ещё нет ни одной внеплановой работы с
 * origin=q_finding. Совпадение сперва по `q_ref === requirement.id` (точная
 * ссылка); запись без `q_ref` засчитывается по контуру — терпимо к тому, что
 * генератор мог не проставить ссылку, но не наоборот (не считает выполненным
 * то, что ссылается на другое предписание). Сортировка — по дате выдачи, от
 * старых к новым: первая запись в списке — самая давняя, то есть самая
 * актуальная как «ещё не внесено».
 */
export function openQuarantineRequirements(
  contourIds: string[],
  statuses: QuarantineStatus[],
  extraWorks: ExtraWork[],
): OpenQuarantineRequirement[] {
  const contourSet = new Set(contourIds);
  const fulfilledByRef = new Set(
    extraWorks.filter((w) => w.origin === 'q_finding' && w.q_ref).map((w) => w.q_ref as string),
  );
  const fulfilledByContour = new Set(
    extraWorks.filter((w) => w.origin === 'q_finding' && !w.q_ref).map((w) => w.contour_id),
  );

  const out: OpenQuarantineRequirement[] = [];
  for (const status of statuses) {
    if (!contourSet.has(status.contour_id)) continue;
    const requirement = status.requirement;
    if (!requirement) continue;
    if (fulfilledByRef.has(requirement.id)) continue;
    if (fulfilledByContour.has(status.contour_id)) continue;
    out.push({ contourId: status.contour_id, requirement });
  }
  return out.sort((a, b) => a.requirement.issued_on.localeCompare(b.requirement.issued_on));
}
